<template>
  <Card v-if="loading">
    <template v-slot:content>
      <div class="p-grid">
        <div class="p-col-3"><Skeleton width="100%" height="30px" /></div>
        <div class="p-col-9"></div>
        <div class="p-col-12" v-for="index in 3" :key="index">
          <Skeleton width="100%" height="30px" />
        </div>
      </div>
    </template>
  </Card>
  <Card v-else>
    <template v-slot:title>Histórico de Peso</template>
    <template v-slot:content>
      <div id="bovine-history-table">
        <DataTable :value="history" responsiveLayout="stack">
          <Column header="Data" field="createdAt" :sortable="true">
            <template #body="props">
              <span v-if="!props.data.editing">
                {{ $dayjs(props.data.createdAt).format('DD/MM/YYYY') }}
              </span>
              <Calendar
                v-else
                v-model="props.data.createdAt"
                dateFormat="dd/mm/yy"
                showIcon
                :monthNavigator="true"
                :yearNavigator="true"
                :yearRange="dateRange"
                :locale="$locales.calendar.ptBR"
                :showOnFocus="false"
                appendTo="body"
                v-mask="'##/##/####'"
              />
            </template>
          </Column>
          <Column header="Peso" field="weight" :sortable="true">
            <template #body="props">
              <span v-if="!props.data.editing">
                {{ props.data.weight ? $toDecimal(props.data.weight) : '-' }}
              </span>
              <span v-else>
                <InputNumber
                  v-model="props.data.weight"
                  mode="decimal"
                  :minFractionDigits="2"
                  @keyup.enter="saveEdit(props.data)"
                  @keyup.esc="cancelEdit(props.data)"
                />
              </span>
            </template>
          </Column>
          <Column header="Ganho" field="weightPercent" :sortable="true">
            <template #body="props">
              <span :style="getWeightStyle(props.data.weightPercent)">
                {{ getWeightText(props.data) }}
              </span>
            </template>
          </Column>
          <Column header="GMD" field="averageDailyGain" :sortable="true">
            <template #body="props">
              <span>
                {{
                  props.data.averageDailyGain
                    ? $toDecimal(props.data.averageDailyGain)
                    : '-'
                }}
              </span>
            </template>
          </Column>
          <Column header="Ações" alignFrozen="right" :frozen="true">
            <template #body="props">
              <span v-if="!props.data.editing">
                <Button
                  type="button"
                  icon="pi pi-pencil"
                  class="p-button-text p-button-secondary p-mr-1"
                  @click="initEdit(props.data)"
                ></Button>
                <Button
                  type="button"
                  icon="pi pi-trash"
                  class="p-button-text p-button-secondary"
                  :disabled="!$ability.can('delete', 'Bovine')"
                  @click="deleteItem(props.data)"
                ></Button>
              </span>
              <span v-else>
                <Button
                  type="button"
                  icon="pi pi-check"
                  class="p-button-text p-button-secondary p-mr-1"
                  @click="saveEdit(props.data)"
                ></Button>
                <Button
                  type="button"
                  icon="pi pi-times"
                  class="p-button-text p-button-secondary"
                  @click="cancelEdit(props.data)"
                ></Button>
              </span>
            </template>
          </Column>
        </DataTable>
        <Paginator
          v-if="pagination.pages > 1"
          :rows="pageSize"
          :totalRecords="pagination.total"
          @page="onPageChange"
        ></Paginator>
      </div>
    </template>
  </Card>
</template>

<script>
import { BovineApi } from '../../../services/api';

export default {
  name: 'BovineHistoryCard',
  props: {
    id: Number,
  },
  data() {
    return {
      pageSize: 10,
      history: [],
      loading: false,
      pagination: {
        total: 0,
        current: 1,
        pages: 1,
      },
      dateRange: `${this.$dayjs().subtract(10, 'y').format('YYYY')}:${this.$dayjs().add(1, 'y').format('YYYY')}`,
    };
  },
  async created() {
    await this.fetchHistory();
  },
  methods: {
    async fetchHistory(query = {}) {
      try {
        this.loading = true;
        const { results: history, pagination } = await BovineApi.fetchHistory(
          this.id,
          {
            pageSize: this.pageSize,
            ...query,
          }
        );
        this.history = history;
        this.pagination = pagination;
      } finally {
        this.loading = false;
      }
    },
    async onPageChange({ page }) {
      await this.fetchHistory({ page: page + 1 });
    },
    getWeightText({ weightPercent, weightDiff }) {
      if (!weightPercent) {
        return '-';
      }
      const prefix = weightPercent > 0 ? '+' : '-';
      return `${prefix} ${this.$toDecimal(
        Math.abs(weightDiff)
      )} (${this.$toDecimal(Math.abs(weightPercent))}%)`;
    },
    getWeightStyle(value) {
      if (!value) {
        return {};
      }
      return {
        color: value > 0 ? '#558B2F' : '#d32f2f',
      };
    },
    initEdit(record) {
      record.oldWeight = record.weight;
      record.oldCreatedAt = record.createdAt ? new Date(record.createdAt) : null;
      record.createdAt = record.createdAt ? new Date(record.createdAt) : new Date();
      record.editing = true;
    },
    cancelEdit(record) {
      record.weight = record.oldWeight;
      record.createdAt = record.oldCreatedAt;
      record.editing = false;
    },
    async deleteItem(record) {
      try {
        this.loading = true;
        await BovineApi.deleteHistory(this.id, record.id);
        await this.fetchHistory();
        record.editing = false;
      } finally {
        this.loading = false;
      }
    },
    async saveEdit(record) {
      try {
        this.loading = true;
        const createdAt = record.createdAt
          ? (record.createdAt instanceof Date ? record.createdAt.toISOString() : record.createdAt)
          : undefined;
        await BovineApi.updateHistory(this.id, record.id, {
          weight: record.weight,
          createdAt,
        });
        await this.fetchHistory();
        record.editing = false;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
#bovine-history-table >>> .p-datatable-scrollable-body {
  min-height: 320px !important;
}

#bovine-history-table >>> .p-inputnumber-input,
#bovine-history-table >>> .p-calendar .p-inputtext {
  width: 100%;
}
</style>